import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import {
  PERSONALIZATION_DEFAULT_CONFIG,
  TABS_FILTERS,
} from '../../../shared/constants';
import { useInvertedLocalize, usePersonalization } from '../../../shared/hooks';
import { type TabFilterState } from '../../../shared/types';
import { useLazyGetTabCountQuery } from '../data-layer/violationsTabApi';
import { type GetTabCountApiResponse } from '../data-layer/violationsTabTypes';
import { violationsTabActions } from '../data-store/violationsTabSlice';

/**
 * Custom hook for managing tab filters in the violations tab
 * @returns {Object} An object containing filter initialization methods
 * @returns {Function} returns.initializeTabFilters - Function to initialize tab filters
 *
 * @example
 * const { initializeTabFilters } = useTabFilters();
 * const success = initializeTabFilters();
 */
const useTabFilters = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { getPersonlization } = usePersonalization();

  const [fetchTabCount] = useLazyGetTabCountQuery();

  const defaultTabValue = useMemo(() => {
    const personalization = getPersonlization();

    return PERSONALIZATION_DEFAULT_CONFIG.saveTab &&
      personalization &&
      personalization.tab
      ? personalization.tab
      : null;
  }, [getPersonlization]);

  /**
   * Initializes the tab filters with predefined filter values
   * @returns {boolean} Returns true if initialization is successful, false otherwise
   */
  const initializeTabFilters = useCallback(
    async (currentTabInAction?: string | null) => {
      try {
        const response = (await fetchTabCount().unwrap()) as
          | GetTabCountApiResponse
          | undefined;

        if (!response) {
          Logger.log('No response received for tab count');
        }

        const tabCountArr: TabFilterState[] = Object.values(TABS_FILTERS)
          .map((tab) => ({
            ...tab,
            label: l(tab.label),
            count: response
              ? Number(response[tab.value as keyof GetTabCountApiResponse])
              : 0,
            isInitial: currentTabInAction
              ? tab.value === currentTabInAction
              : tab.value === defaultTabValue,
          }))
          .filter((tab) => tab.count !== 0 || !tab.isZeroBased);

        dispatch(violationsTabActions.setTabFilters(tabCountArr));

        return true;
      } catch {
        Logger.log('Error fetching tab count');

        return false;
      }
    },
    [l, dispatch, fetchTabCount, defaultTabValue]
  );

  return { initializeTabFilters };
};

// eslint-disable-next-line import/prefer-default-export
export { useTabFilters };
